import { motion } from 'framer-motion'
import { Target, Eye, MapPin, Calendar, Users, Award } from 'lucide-react'
import { company, journey, coreValues } from '@data/company'
import AnimatedSection from '@components/AnimatedSection'

export default function About() {
  return (
    <>
      {/* Page Header */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-martimma-red/10 to-transparent" />
        <div className="section-padding max-w-7xl mx-auto relative z-10">
          <AnimatedSection>
            <span className="text-martimma-red text-sm font-semibold tracking-wider uppercase">About Us</span>
            <h1 className="heading-xl mt-4 mb-6">A Multi-Sector Nigerian Powerhouse</h1>
            <p className="body-lg max-w-3xl">
              {company.name} is a fully integrated multi-sector company operating across Nigeria's energy, construction, agriculture, and logistics industries. Incorporated under CAC (CAMA 1990), we are proudly Nigerian and future-focused.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 relative">
        <div className="section-padding max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <AnimatedSection>
              <div className="glass-card p-10 h-full border-l-4 border-l-martimma-red">
                <div className="w-14 h-14 rounded-2xl bg-martimma-red/10 flex items-center justify-center mb-6">
                  <Eye className="w-7 h-7 text-martimma-red" />
                </div>
                <h2 className="heading-md text-white mb-4">Our Vision</h2>
                <p className="text-gray-300 leading-relaxed text-lg">
                  To be the Leading Integrated African Energy and Services Company, recognized by its People, Excellence and Values.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.15}>
              <div className="glass-card p-10 h-full border-l-4 border-l-martimma-yellow">
                <div className="w-14 h-14 rounded-2xl bg-martimma-yellow/10 flex items-center justify-center mb-6">
                  <Target className="w-7 h-7 text-martimma-yellow" />
                </div>
                <h2 className="heading-md text-white mb-4">Our Mission</h2>
                <p className="text-gray-300 leading-relaxed text-lg">
                  To be the preferred service provider in the hearts and minds of our customers, recognized for the reliability, quality, cleanliness, and safety of our products and services across all sectors we serve.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Company Stats */}
      <section className="py-20 bg-martimma-dark-light">
        <div className="section-padding max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Calendar, value: company.founded.toString(), label: 'Year Established' },
              { icon: MapPin, value: '3', label: 'Strategic Locations' },
              { icon: Users, value: '4', label: 'Business Sectors' },
              { icon: Award, value: '6', label: 'Geopolitical Zones' },
            ].map((stat, index) => (
              <AnimatedSection key={stat.label} delay={index * 0.1}>
                <div className="text-center p-6">
                  <stat.icon className="w-8 h-8 text-martimma-red mx-auto mb-4" />
                  <div className="text-4xl font-bold text-martimma-yellow mb-2">{stat.value}</div>
                  <div className="text-gray-400">{stat.label}</div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Our Journey - Timeline */}
      <section className="py-24 relative">
        <div className="section-padding max-w-5xl mx-auto">
          <AnimatedSection className="text-center mb-16">
            <span className="text-martimma-yellow text-sm font-semibold tracking-wider uppercase">Our History</span>
            <h2 className="heading-lg mt-4">A Decade of Growth & Diversification</h2>
          </AnimatedSection>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-martimma-red via-martimma-yellow to-martimma-red md:-translate-x-px" />

            {journey.map((item, index) => (
              <AnimatedSection key={item.year} delay={index * 0.1}>
                <div className={`relative flex items-start gap-8 mb-12 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}>
                  {/* Timeline dot */}
                  <div className="absolute left-4 md:left-1/2 w-4 h-4 rounded-full bg-martimma-red border-4 border-martimma-dark md:-translate-x-2 z-10" />

                  {/* Content */}
                  <div className={`ml-12 md:ml-0 md:w-1/2 ${
                    index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'
                  }`}>
                    <div className="glass-card p-6 hover:border-martimma-red/30 transition-colors">
                      <span className="text-martimma-yellow font-bold text-lg">{item.year}</span>
                      <h3 className="text-white font-semibold text-xl mt-2 mb-3">{item.title}</h3>
                      <p className="text-gray-400 text-sm">{item.description}</p>
                    </div>
                  </div>
                  <div className="hidden md:block md:w-1/2" />
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values Detailed */}
      <section className="py-24 bg-martimma-dark-light">
        <div className="section-padding max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-16">
            <span className="text-martimma-red text-sm font-semibold tracking-wider uppercase">What We Stand For</span>
            <h2 className="heading-lg mt-4">TEST IQS+ Core Values</h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {coreValues.map((value, index) => (
              <AnimatedSection key={value.title} delay={index * 0.05}>
                <motion.div
                  whileHover={{ y: -5 }}
                  className="glass-card p-6 text-center group cursor-default"
                >
                  <div className="w-14 h-14 rounded-xl bg-martimma-red/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-martimma-red/20 transition-colors">
                    <span className="text-martimma-red font-bold text-xl">{value.title[0]}</span>
                  </div>
                  <h3 className="text-white font-semibold mb-2">{value.title}</h3>
                  <p className="text-gray-500 text-sm">{value.description}</p>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
